import React, { useState, useEffect, useRef } from "react";

export default function App() {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [player, setPlayer] = useState("X");
  const [winner, setWinner] = useState(null);
  const [winningLine, setWinningLine] = useState([]);
  const [fadeOut, setFadeOut] = useState(false);
  const [score, setScore] = useState({ X: 0, O: 0 });
  const [showWinScreen, setShowWinScreen] = useState(false);
  const [winScreenClosing, setWinScreenClosing] = useState(false);
  const boardRef = useRef(null);
  const [cellSize, setCellSize] = useState(0);

  const lines = [
    [0, 1, 2],
    [3, 4, 5],
    [6, 7, 8],
    [0, 3, 6],
    [1, 4, 7],
    [2, 5, 8],
    [0, 4, 8],
    [2, 4, 6],
  ];

  useEffect(() => {
    const updateSize = () => {
      if (boardRef.current) setCellSize(boardRef.current.clientWidth / 3);
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const checkWinner = (newBoard) => {
    for (let [a, b, c] of lines) {
      if (
        newBoard[a] &&
        newBoard[a] === newBoard[b] &&
        newBoard[a] === newBoard[c]
      ) {
        setWinningLine([a, b, c]);
        return newBoard[a];
      }
    }
    return null;
  };

  const handleClick = (index) => {
    if (board[index] || winner) return;
    const newBoard = [...board];
    newBoard[index] = player;
    setBoard(newBoard);

    const gameWinner = checkWinner(newBoard);
    if (gameWinner) {
      setWinner(gameWinner);
      setScore((prev) => ({ ...prev, [gameWinner]: prev[gameWinner] + 1 }));
      setTimeout(() => setShowWinScreen(true), 600);
    } else if (!newBoard.includes(null)) {
      setWinner("draw");
      setTimeout(() => setShowWinScreen(true), 600);
    } else {
      setPlayer(player === "X" ? "O" : "X");
    }
  };

  const resetGame = () => {
    setFadeOut(true);
    setWinScreenClosing(true);
    setTimeout(() => {
      let nextStarter = "X";
      if (winner && winner !== "draw")
        nextStarter = winner === "X" ? "O" : "X";
      if (winner === "draw") nextStarter = Math.random() > 0.5 ? "X" : "O";

      setBoard(Array(9).fill(null));
      setPlayer(nextStarter);
      setWinner(null);
      setWinningLine([]);
      setFadeOut(false);
      setShowWinScreen(false);
      setWinScreenClosing(false);
    }, 400);
  };

  const cellColor = (val) => (val === "X" ? "text-blue-400" : "text-pink-400");

  const lineStyle = () => {
    if (winningLine.length !== 3 || cellSize === 0) return {};
    const points = winningLine.map((index) => {
      const row = Math.floor(index / 3);
      const col = index % 3;
      return {
        x: col * cellSize + cellSize / 2,
        y: row * cellSize + cellSize / 2,
      };
    });
    const start = points[0];
    const end = points[2];
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    return {
      position: "absolute",
      top: `${start.y}px`,
      left: `${start.x}px`,
      width: `${length}px`,
      height: "4px",
      transform: `rotate(${angle}deg)`,
      transformOrigin: "0 50%",
    };
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white px-4 relative overflow-hidden">
      <div className="flex gap-8 mb-6 text-xl font-semibold">
        <div className="flex flex-col items-center">
          <span className="text-blue-400">X</span>
          <span>{score.X}</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-pink-400">O</span>
          <span>{score.O}</span>
        </div>
      </div>

      <h1 className="text-3xl font-bold mb-4">Kółko i Krzyżyk</h1>

      <div
        className="relative w-full max-w-sm"
        ref={boardRef}
        style={{ aspectRatio: "1/1" }}
      >
        <div className="grid grid-cols-3 grid-rows-3 w-full h-full">
          {board.map((cell, i) => (
            <button
              key={i}
              onClick={() => handleClick(i)}
              className="flex items-center justify-center border border-gray-700 w-full h-full"
            >
              {cell && (
                <span
                  className={`text-4xl font-bold ${cellColor(cell)} ${
                    fadeOut ? "animate-fadeOut" : "animate-scale-fade"
                  }`}
                >
                  {cell}
                </span>
              )}
            </button>
          ))}
        </div>

        {winner && winningLine.length === 3 && (
          <div style={lineStyle()}>
            <div
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: winner === "X" ? "#60A5FA" : "#F472B6",
                transformOrigin: "0 50%",
              }}
              className={fadeOut ? "animate-lineFadeOut" : "animate-lineGrow"}
            />
          </div>
        )}
      </div>

      {(
        <div className="mt-4 text-lg">
          {winner ? (
            winner === "draw" ? (
              <p>Remis!</p>
            ) : (
              <p>
                Zwycięzca:{" "}
                <span
                  className="font-bold"
                  style={{ color: winner === "X" ? "#60A5FA" : "#F472B6" }}
                >
                  {winner}
                </span>
              </p>
            )
          ) : (
            <p>
              Tura gracza:{" "}
              <span
                className="font-bold"
                style={{ color: player === "X" ? "#60A5FA" : "#F472B6" }}
              >
                {player}
              </span>
            </p>
          )}
        </div>
      )}

      {showWinScreen && (
        <div
          className={`absolute inset-0 bg-black/80 flex flex-col items-center justify-center text-center ${
            winScreenClosing ? "animate-fadeOutModal" : "animate-fadeIn"
          }`}
        >
          <h2 className="text-3xl font-bold mb-4">
            {winner === "draw" ? "Remis!" : "Wygrana"}
          </h2>
          {winner !== "draw" && (
            <span
              className={`text-[80px] font-bold ${
                winner === "X" ? "text-blue-400" : "text-pink-400"
              } animate-scale-fade`}
            >
              {winner}
            </span>
          )}
          <button
            onClick={resetGame}
            className="mt-6 bg-blue-500 hover:bg-blue-600 px-6 py-2 rounded-xl font-semibold text-white transition-all"
          >
            Nowa runda
          </button>
        </div>
      )}

      <style>{`
        @keyframes scaleFade {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-scale-fade {
          animation: scaleFade 0.3s ease-in-out forwards;
        }

        @keyframes fadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-in-out forwards;
        }

        @keyframes fadeOutModal {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        .animate-fadeOutModal {
          animation: fadeOutModal 0.4s ease-in-out forwards;
        }

        @keyframes fadeOut {
          0% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0); }
        }
        .animate-fadeOut {
          animation: fadeOut 0.3s forwards;
        }

        @keyframes lineGrow {
          0% { transform: scaleX(0); opacity:0; }
          100% { transform: scaleX(1); opacity:1; }
        }
        .animate-lineGrow {
          transform-origin: 0 50%;
          animation: lineGrow 0.5s ease-out forwards;
        }

        @keyframes lineFadeOut {
          0% { opacity: 1; transform: scaleX(1); }
          100% { opacity: 0; transform: scaleX(0); }
        }
        .animate-lineFadeOut {
          transform-origin: 0 50%;
          animation: lineFadeOut 0.3s forwards;
        }
      `}</style>
    </div>
  );
}